<script>
  import { _ } from 'svelte-i18n';
  import snarkdown from 'snarkdown'

  import { api } from "./../libs/api.js";

  import Sidebar from "./../components/Sidebar.svelte";
  import Topbar from "./../components/Topbar.svelte";
  import Step from "./../components/Step.svelte";
  import LectureStep from "./../components/LectureStep.svelte";
  import ExBlankStep from "./../components/ExBlankStep.svelte";
  import ExOrderStep from "./../components/ExOrderStep.svelte";
  import ExTFStep from "./../components/ExTFStep.svelte";
  import ExChoiceStep from "./../components/ExChoiceStep.svelte";
  import ExOpenStep from "./../components/ExOpenStep.svelte";
  import StepHeader from "./../components/StepHeader.svelte";
  import DataSaver from "./../components/DataSaver.svelte";

  export let course_id;

  let current_step = 0;

  $: course_id, current_step = 0; // reset current_step on changes to course_id

  function get_modules(course) {
    let modules = []
    if (course.data[0].modules.length > 0)
      modules = course.data[0].modules.map(x => x.modules_id);

    return modules;
  }

  function handle_prev(ev) {
    current_step--;
  }

  function handle_next(ev) {
    current_step++;
  }

  function handle_arrow_keys(ev) {
    const scroll_amount = 30;
    let target_name = ev.target.tagName.toLowerCase();
    let elements_step = document.getElementsByClassName('class-step');

    if (target_name != 'input' && target_name != 'textarea') {

      if (ev.keyCode == 37 && current_step > 0) {
        handle_prev();
      }
      else if (ev.keyCode == 38) {
        elements_step[current_step].scrollBy({ top: -scroll_amount});
      }
      else if (ev.keyCode == 39 && current_step < elements_step.length -1) {
        handle_next();
      }
      else if (ev.keyCode == 40) {
        elements_step[current_step].scrollBy({ top: scroll_amount});
      }

    }
  }

</script>

<div>

  <Sidebar current_course="{course_id}">

      {#await api.get_course(course_id) then course}
        {@const modules = get_modules(course)}

        <Topbar bind:current_step modules={get_modules(course)} />

        <div class="flex flex-auto transition-transform h-0" style="transform: translateX({current_step * -100}%)">

          <!-- Title of the course -->
          <Step justified nextFn="{handle_next}">
            <h2 class="text-6xl text-center px-4">{course.data[0].name}</h2>
          </Step>

          {#each modules as mod, i}

            <!-- Title of the module -->
            <Step justified nextFn="{handle_next}">
              <StepHeader course={course.data[0]} {mod} />

              <div class="flex mt-16 w-4/5 justify-around">
                <div class="card w-2/3 border-2 border-secondary px-6">
                  <span class="absolute -top-6 -left-6 border-2 border-secondary text-2xl bg-base-100">
                    <img class="w-12 h-12" src="/assets/info.svg" />
                  </span>

                  <div class="card-body text-justify">
                    <p>{@html snarkdown(mod.description)}</p>
                  </div>
                </div>
              </div>
            </Step>

            <!-- Aims/Remarks about the module -->
            <Step prevFn="{handle_prev}" nextFn="{handle_next}">
              <StepHeader course={course.data[0]} {mod} />

              <div class="flex mt-16 w-4/5 justify-around">
                <div class="card w-80 border-2 border-secondary">
                  <span class="absolute -top-6 -left-6 border-2 border-secondary text-2xl bg-base-100">
                    <img class="w-12 h-12" src="/assets/info.svg" />
                  </span>

                  <div class="card-body">
                    <h4 class="uppercase"> {$_('course.at_the_end')} </h4>
                    <div class="prose">{@html snarkdown(mod.aims)}</div>
                  </div>
                </div>

                <div class="card w-80 border-2 border-secondary">
                  <span class="absolute -top-6 -left-6 border-2 border-secondary text-2xl bg-base-100">
                    <img class="w-12 h-12" src="/assets/info.svg" />
                  </span>

                  <div class="card-body">
                    <h4 class="uppercase"> {$_('course.why_important')} </h4>
                    <div class="prose">{@html snarkdown(mod.remarks)}</div>
                  </div>
                </div>
              </div>
            </Step>

            {#if mod.steps}
              {#each mod.steps as stp}

                <!-- Lecture step -->
                {#if stp.type == "lecture"}
                  <LectureStep course={course.data[0]} {mod} step="{stp}" prevFn="{handle_prev}" nextFn="{handle_next}" />
                {:else if stp.type == "ex_blank"}
                  <ExBlankStep course={course.data[0]} {mod} step="{stp}" prevFn="{handle_prev}" nextFn="{handle_next}" />
                {:else if stp.type == "ex_order"}
                  <ExOrderStep course={course.data[0]} {mod} step="{stp}" prevFn="{handle_prev}" nextFn="{handle_next}" />
                {:else if stp.type == "ex_tf"}
                  <ExTFStep course={course.data[0]} {mod} step="{stp}" prevFn="{handle_prev}" nextFn="{handle_next}" />
                {:else if stp.type == "ex_choice"}
                  <ExChoiceStep course={course.data[0]} {mod} step="{stp}" prevFn="{handle_prev}" nextFn="{handle_next}" />
                {:else if stp.type == "ex_open"}
                  <ExOpenStep course={course.data[0]} {mod} step="{stp}" prevFn="{handle_prev}" nextFn="{handle_next}" />
                {/if}

              {/each}
            {:else}
              <Step justified prevFn="{handle_prev}" >
                <div class="alert alert-error shadow-lg w-96">
                  <div>
                    <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span> {$_('course.bad_module')} </span>
                  </div>
                </div>
              </Step>
            {/if}

          <!-- End of the module -->
          <Step justified prevFn="{handle_prev}" nextFn="{i != modules.length-1 ? handle_next : undefined}">
            <img class="w-96 my-10 mx-auto" src="/assets/Victory.svg" />
            <DataSaver {mod} />
          </Step>

          {/each}
        </div>

      {/await}

  </Sidebar>

</div>

<svelte:window on:keydown={handle_arrow_keys} />